import { useState, useEffect, useCallback, useRef } from "react";
import { Maximize2, Minimize2, Map, RefreshCw } from "lucide-react";

export default function WorldMapTab({ onFullscreenChange }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshKey,   setRefreshKey]   = useState(0);
  const [mapLoading,   setMapLoading]   = useState(true);

  const containerRef = useRef(null);

  // ── Map iframe loading state ─────────────────────────────────────────────
  useEffect(() => {
    setMapLoading(true);
    const t = setTimeout(() => setMapLoading(false), 1800);
    return () => clearTimeout(t);
  }, [refreshKey]);

  // ── Fullscreen handling ──────────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => {
      const entered = !!document.fullscreenElement;
      setIsFullscreen(entered);
      onFullscreenChange?.(entered);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [onFullscreenChange]);

  const enterFullscreen = useCallback(() => containerRef.current?.requestFullscreen?.(), []);
  const exitFullscreen  = useCallback(() => document.exitFullscreen?.(), []);

  const handleRefresh = () => setRefreshKey(k => k + 1);

  return (
    <>
      <style>{`
        @keyframes dynmap-spin { to { transform: rotate(360deg); } }
        @keyframes dynmap-pulse-ring {
          0%   { transform: scale(0.9); opacity: 0.7; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .dynmap-btn:hover {
          background: rgba(74,222,128,.08) !important;
          color: #4ade80 !important;
          border-color: rgba(74,222,128,.25) !important;
        }
        #dynmap-container:fullscreen {
          display: flex; flex-direction: column;
          background: #060d18; width: 100vw; height: 100vh;
        }
        #dynmap-container:fullscreen .dynmap-iframe-wrap {
          flex: 1; min-height: 0;
        }
        #dynmap-container:fullscreen .dynmap-iframe-wrap iframe {
          width: 100% !important; height: 100% !important;
        }
        @media (max-width: 768px) {
          .dynmap-iframe-wrap { height: 380px !important; }
        }
      `}</style>

      {/* ── Section header ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 10, letterSpacing: 5, color: "#4ade80", textTransform: "uppercase", marginBottom: 4 }}>
          Live Territory Control
        </div>
        <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 22, color: "var(--toa-text,#e8e0f0)", marginBottom: 4 }}>
          World Map
        </div>
        <div style={{ fontSize: 13, color: "var(--toa-muted,#6b6080)" }}>
          Explore the realm in real time.
        </div>
      </div>

      {/* ── Map card ── */}
      <div id="dynmap-container" ref={containerRef} style={{
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(74,222,128,.13)",
        borderRadius: isFullscreen ? 0 : 14,
        overflow: "hidden",
        boxShadow: "0 0 60px rgba(0,0,0,.4)",
        display: "flex", flexDirection: "column",
      }}>

        {/* Toolbar */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px", flexShrink: 0,
          borderBottom: "1px solid rgba(74,222,128,.08)",
          background: "rgba(0,0,0,.3)",
          gap: 10, flexWrap: "wrap",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "5px 12px", borderRadius: 20,
            background: "rgba(74,222,128,.15)",
            border: "1px solid rgba(74,222,128,.35)",
            color: "#4ade80",
            fontFamily: "'Cinzel',serif", fontSize: 9, letterSpacing: 1, textTransform: "uppercase",
          }}>
            <span style={{ position: "relative", width: 8, height: 8, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 6px #4ade80", display: "inline-block" }}/>
              <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#4ade80", animation: "dynmap-pulse-ring 1.6s ease-out infinite" }}/>
            </span>
            <Map style={{ width: 11, height: 11 }}/> Realm Map
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button className="dynmap-btn" onClick={handleRefresh}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 6, background: "transparent", border: "1px solid rgba(255,255,255,.07)", color: "var(--toa-muted,#6b6080)", cursor: "pointer", transition: ".15s", fontFamily: "'Cinzel',serif", fontSize: 9, textTransform: "uppercase", letterSpacing: 1 }}>
              <RefreshCw style={{ width: 11, height: 11 }}/> Refresh
            </button>
            <button className="dynmap-btn"
              onClick={isFullscreen ? exitFullscreen : enterFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 6, background: "transparent", border: "1px solid rgba(255,255,255,.07)", color: "var(--toa-muted,#6b6080)", cursor: "pointer", transition: ".15s" }}>
              {isFullscreen ? <Minimize2 style={{ width: 13, height: 13 }}/> : <Maximize2 style={{ width: 13, height: 13 }}/>}
            </button>
          </div>
        </div>

        {/* Map iframe */}
        <div className="dynmap-iframe-wrap" style={{ position: "relative", background: "#060d18", height: 800 }}>
          {mapLoading && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(160deg,#060d18,#0a1208)", gap: 14,
            }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", border: "2px solid rgba(74,222,128,.15)", borderTop: "2px solid #4ade80", animation: "dynmap-spin 0.8s linear infinite" }}/>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 10, letterSpacing: 3, color: "rgba(74,222,128,.5)", textTransform: "uppercase" }}>Loading Realm Map…</div>
            </div>
          )}
          <iframe
            key={refreshKey}
            src="/map"
            width="100%"
            height="100%"
            style={{ border: "none", display: "block", opacity: mapLoading ? 0 : 1, transition: "opacity .5s" }}
          />
        </div>
      </div>
    </>
  );
}